import { FacilityTypes } from 'src/enums'
import { ILocality, ILocalityGroup } from 'src/generated-types'
import { IServiceStation } from 'src/generated-types/service-station'
import { IStorage } from 'src/generated-types/storage-types'

export interface IFacilityFilters {
  localityIds?: number[]
  serviceStationIds?: number[]
  storageIds?: number[]
  localityGroupIds?: number[]
}

export interface INewsFacilityFilterProps {
  customerId: number
  facilityFilters?: IFacilityFilters
  onFiltersChange: (filters: IFacilityFilters) => void
  localitiesData?: ILocality[]
  serviceStationsData?: IServiceStation[]
  storagesData?: IStorage[]
  localityGroupsData?: ILocalityGroup[]
  isLoadingLocalities?: boolean
  isLoadingServiceStations?: boolean
  isLoadingStorages?: boolean
  isLoadingLocalityGroups?: boolean
}

export const buildFilters = (filterType: FacilityTypes | '', items: number[]): IFacilityFilters => {
  const filters: IFacilityFilters = {}
  if (filterType === FacilityTypes.locality && items.length > 0) {
    filters.localityIds = items
  } else if (filterType === FacilityTypes.serviceStation && items.length > 0) {
    filters.serviceStationIds = items
  } else if (filterType === FacilityTypes.storage && items.length > 0) {
    filters.storageIds = items
  } else if (filterType === FacilityTypes.localityGroup && items.length > 0) {
    filters.localityGroupIds = items
  }
  return filters
}

export const getFilterTypeLabel = (
  translate: (key: string, options?: any) => string,
  facilityFilters?: IFacilityFilters,
): string => {
  if (facilityFilters?.localityIds?.length) {
    return `${translate('Locality', { ns: 'common' })} (${facilityFilters.localityIds.length})`
  }
  if (facilityFilters?.serviceStationIds?.length) {
    return `${translate('ServiceStation', { ns: 'common' })} (${facilityFilters.serviceStationIds.length})`
  }
  if (facilityFilters?.storageIds?.length) {
    return `${translate('storage', { ns: 'page-container' })} (${facilityFilters.storageIds.length})`
  }
  if (facilityFilters?.localityGroupIds?.length) {
    return `${translate('locality-group', { ns: 'common' })} (${facilityFilters.localityGroupIds.length})`
  }
  return ''
}

export const getParentMenuItems = (
  translate: (key: string, options?: any) => string,
  facilityFilters?: IFacilityFilters,
) => [
  {
    id: FacilityTypes.locality,
    label: translate('locality', { ns: 'common' }),
    getSelectedItems: () => (facilityFilters?.localityIds?.length ? facilityFilters.localityIds : []),
  },
  {
    id: FacilityTypes.localityGroup,
    label: translate('locality-group', { ns: 'common' }),
    getSelectedItems: () => (facilityFilters?.localityGroupIds?.length ? facilityFilters.localityGroupIds : []),
  },
  {
    id: FacilityTypes.serviceStation,
    label: translate('ServiceStation', { ns: 'common' }),
    getSelectedItems: () => (facilityFilters?.serviceStationIds?.length ? facilityFilters.serviceStationIds : []),
  },
  {
    id: FacilityTypes.storage,
    label: translate('Storage', { ns: 'page-container' }),
    getSelectedItems: () => (facilityFilters?.storageIds?.length ? facilityFilters.storageIds : []),
  },
]

export const createItemHandlers = (
  itemId: number,
  selectedFilterType: FacilityTypes | '',
  selectedItems: number[],
  setSelectedItems: (items: number[]) => void,
  onFiltersChange: (filters: IFacilityFilters) => void,
) => ({
  onCheckBoxChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    event.stopPropagation()
    event.preventDefault()
    const newItems = checked ? [...selectedItems, itemId] : selectedItems.filter(id => id !== itemId)
    setSelectedItems(newItems)
    onFiltersChange(buildFilters(selectedFilterType, newItems))
  },
  onClick: (event: React.MouseEvent) => {
    const target = event.target as HTMLElement
    const isCheckboxClick = target instanceof HTMLInputElement && target.type === 'checkbox'
    if (!isCheckboxClick) {
      const newItems = selectedItems.includes(itemId)
        ? selectedItems.filter(id => id !== itemId)
        : [...selectedItems, itemId]
      setSelectedItems(newItems)
      onFiltersChange(buildFilters(selectedFilterType, newItems))
    }
  },
})

export const mapItems = (
  items: Array<{ id: number; name: string }>,
  selectedItems: number[],
  createHandlers: (itemId: number) => any,
) =>
  items.map(item => ({
    id: item.id,
    label: item.name,
    checked: selectedItems.includes(item.id),
    ...createHandlers(item.id),
  }))

export const getSelectedFilterState = (
  facilityFilters?: IFacilityFilters,
): { filterType: FacilityTypes | ''; items: number[] } => {
  if (facilityFilters?.localityIds?.length) {
    return { filterType: FacilityTypes.locality, items: facilityFilters.localityIds }
  }
  if (facilityFilters?.serviceStationIds?.length) {
    return { filterType: FacilityTypes.serviceStation, items: facilityFilters.serviceStationIds }
  }
  if (facilityFilters?.storageIds?.length) {
    return { filterType: FacilityTypes.storage, items: facilityFilters.storageIds }
  }
  if (facilityFilters?.localityGroupIds?.length) {
    return { filterType: FacilityTypes.localityGroup, items: facilityFilters.localityGroupIds }
  }
  return { filterType: '', items: [] }
}
